import { computed, watch } from "vue";
import type { TableProps } from "./TableProps";
import type { TableRowProps } from "./TableRowProps";
import type { TableContext } from "./useTable.types";
import { parseBoolAttr } from "@/runtime/shared/attrs";

// ─── Module-scope helpers ──────────────────────────────────────────

const getSelectedRowIndices = (tableProps: TableContext["tableProps"]): Set<number> => {
  const raw = tableProps.value?.selectedRows;
  if (!raw) return new Set();
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return new Set(parsed);
  } catch {
    // ignore parse errors
  }
  return new Set();
};

const setSelectedRowIndices = (
  indices: Set<number>,
  tableUid: TableContext["tableUid"],
  stateStore: TableContext["stateStore"],
): void => {
  if (!tableUid.value) return;
  stateStore.setValue<TableProps>(tableUid.value, "selectedRows", JSON.stringify([...indices]));
};

const checkRowSelected = (rowIndex: number, tableProps: TableContext["tableProps"]): boolean => {
  if (rowIndex < 0) return false;
  return getSelectedRowIndices(tableProps).has(rowIndex);
};

const checkRowSelectable = (rowIndex: number, tableProps: TableContext["tableProps"]): boolean => {
  if (rowIndex < 0) return false;
  const mode = tableProps.value?.selectable;
  return mode === "single" || mode === "multiple";
};

const selectRow = (rowIndex: number, ctx: TableContext): void => {
  if (rowIndex < 0) return;
  const mode = ctx.tableProps.value?.selectable;
  if (!mode) return;

  if (mode === "single") {
    setSelectedRowIndices(new Set([rowIndex]), ctx.tableUid, ctx.stateStore);
  } else {
    const next = new Set(getSelectedRowIndices(ctx.tableProps));
    next.add(rowIndex);
    setSelectedRowIndices(next, ctx.tableUid, ctx.stateStore);
  }
};

const toggleRowSelection = (rowIndex: number, ctx: TableContext): void => {
  if (rowIndex < 0) return;
  const mode = ctx.tableProps.value?.selectable;
  if (!mode) return;

  const current = getSelectedRowIndices(ctx.tableProps);

  if (mode === "single") {
    setSelectedRowIndices(current.has(rowIndex) ? new Set() : new Set([rowIndex]), ctx.tableUid, ctx.stateStore);
    return;
  }

  const next = new Set(current);
  if (next.has(rowIndex)) next.delete(rowIndex);
  else next.add(rowIndex);
  setSelectedRowIndices(next, ctx.tableUid, ctx.stateStore);
};

// ─── Composable ────────────────────────────────────────────────────

/**
 * Table selection composable.
 *
 * Manages row selection state (single or multiple mode). Selection is stored
 * as a JSON-serialized array of row indices on the parent table's state.
 */
export const useTableSelection = (ctx: TableContext) => {
  const { context, stateStore, tableProps, currentRowIndex } = ctx;

  const isRowSelected = computed(() => checkRowSelected(currentRowIndex.value, tableProps));
  const isRowSelectable = computed(() => checkRowSelectable(currentRowIndex.value, tableProps));
  const getSelectedRows = computed((): number[] => [...getSelectedRowIndices(tableProps)]);

  const syncRowSelection = () => {
    watch(
      [
        () => {
          if (context.uid === null) return;
          return stateStore.getByUid<TableRowProps>(context.uid)?.props?.selected;
        },
        currentRowIndex,
      ],
      ([newVal, index]) => {
        if (index < 0) return;
        const currentlySelected = checkRowSelected(index, tableProps);
        if (newVal && !currentlySelected) selectRow(index, ctx);
        else if (!newVal && currentlySelected) toggleRowSelection(index, ctx);
      },
      { immediate: true },
    );
  };

  const handleRowClick = (): { rowIndex: number; selected: boolean } | null => {
    if (context.uid === null) return null;
    const componentState = stateStore.getByUid<TableRowProps>(context.uid);
    if (parseBoolAttr(componentState?.props?.disabled, false)) return null;
    if (!isRowSelectable.value) return null;

    toggleRowSelection(currentRowIndex.value, ctx);
    return { rowIndex: currentRowIndex.value, selected: isRowSelected.value };
  };

  return {
    isRowSelected,
    isRowSelectable,
    getSelectedRows,
    syncRowSelection,
    handleRowClick,
  };
};
